import React, { createContext, useState, useEffect } from "react";
import { login as loginV2, changePassword as changePasswordV2 } from "../../utils/services/control/authService";
import { logout as logoutV2 } from "../../utils/services/control/sessionService";
import { getEmployeeInfo, getEmployeeLocations, getRoleModules } from "../../utils/services/control/masterDataService";
import { verifyMfa as verifyMfaApi, consumeMfaRecoveryCode } from "../../utils/services/control/mfaService";
import { isTokenExpired } from "../../utils/services/Auth/isTokenExpired";
import secureStorage from "../../utils/security/secureStorage";
import resetPasswordService from "../../utils/services/Management/resetPassword";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Pre-auth state for MFA flow.
  // Only lives in memory — never persisted. Dura 2 minutos por diseño del backend.
  // Si el usuario recarga la página durante la verificación 2FA, debe volver al login.
  const [preAuth, setPreAuth] = useState(null);
  // Shape: { preAuthToken, employeeId, email, forcePasswordChange, passwordExpired }

  // Load stored user on app start
  // Token in localStorage for API compatibility (77+ services use it)
  // User data in secureStorage (encrypted) for HIPAA compliance
  useEffect(() => {
    const storedUser = secureStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (storedUser && token && !isTokenExpired(token)) {
      // Set immediately from cache to avoid flash of login screen
      setUser(storedUser);
      // Then refresh employee info from backend so mfaEnabled (and other
      // fields) reflect the current server state. Sessions created before
      // the backend exposed mfaEnabled have stale data and would otherwise
      // trigger the /mfa-required gate incorrectly.
      refreshCachedSession(storedUser).finally(() => setLoading(false));
    } else {
      secureStorage.removeItem("user");
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      setUser(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Re-consulta /api/MasterData/employee-info/{id} para refrescar los campos
   * que pueden haber cambiado server-side desde la última sesión (notablemente
   * `mfaEnabled`). Preserva activeRole, locations y modules ya cargados.
   *
   * Si la llamada falla (red, 401 por sesión revocada, etc.) no borramos el
   * user — dejamos que el próximo llamado autenticado reaccione con su propio
   * manejo de errores.
   */
  const refreshCachedSession = async (cachedUser) => {
    if (!cachedUser?.employeeId) return;
    try {
      const rows = await getEmployeeInfo(cachedUser.employeeId);
      const first = Array.isArray(rows) ? rows[0] : null;
      if (!first || !first.employeeId) return;

      const refreshed = {
        ...cachedUser,
        firstName: first.firstName ?? cachedUser.firstName,
        lastName: first.lastName ?? cachedUser.lastName,
        fullName: first.firstName && first.lastName
          ? `${first.firstName} ${first.lastName}`
          : cachedUser.fullName,
        email: first.mail ?? cachedUser.email,
        businessEmail: first.businessEmail ?? cachedUser.businessEmail,
        jobPositionId: first.jobPositionId ?? cachedUser.jobPositionId,
        jobPositionName: first.jobPositionName ?? cachedUser.jobPositionName,
        photo: first.photo ?? cachedUser.photo,
        mfaEnabled: first.mfaEnabled ?? false,
        mfaFrequencyDays: first.mfaFrequencyDays ?? cachedUser.mfaFrequencyDays ?? null,
      };
      persistUser(refreshed);
    } catch (err) {
      console.warn("Could not refresh employee info from cached session:", err);
    }
  };

  /**
   * Persiste userData en localStorage y secureStorage.
   * Centraliza la lógica de almacenamiento para evitar duplicación.
   */
  const persistUser = (userData) => {
    setUser(userData);
    // Token in localStorage for API service compatibility (77+ services)
    localStorage.setItem("token", userData.token);
    // User data encrypted in secureStorage (HIPAA - PHI protection)
    secureStorage.setItem("user", userData);
    // User data in localStorage for legacy compatibility
    localStorage.setItem("user", JSON.stringify(userData));
  };

  /**
   * Ejecuta el flujo post-login (getEmployeeInfo → getEmployeeLocations →
   * getRoleModules) y persiste el userData construido. Se invoca tanto desde
   * el signIn normal (sin MFA) como después de verificar MFA exitosamente.
   *
   * @param {Object} loginData - respuesta de /api/AuthV2/login o /api/Mfa/verify
   *   - token: JWT completo (8h)
   *   - employeeId: ID del empleado autenticado
   *   - sessionId?: GUID de la sesión (solo viene en login directo)
   *   - forcePasswordChange?: boolean
   *   - passwordExpired?: boolean
   *   - email?: email del usuario (viene del pre-auth state en flujo MFA)
   * @returns {Object} userData completo con roles, locations y modules
   */
  const runPostLoginFlow = async (loginData) => {
    // Guardar token inmediatamente (necesario para post-login calls)
    localStorage.setItem("token", loginData.token);

    // Si debe cambiar contraseña → NO hacer post-login calls.
    // Guardar solo lo mínimo para que ChangePasswordPage funcione.
    if (loginData.forcePasswordChange || loginData.passwordExpired) {
      const minimalUserData = {
        token: loginData.token,
        employeeId: loginData.employeeId,
        sessionId: loginData.sessionId,
        forcePasswordChange: loginData.forcePasswordChange,
        passwordExpired: loginData.passwordExpired,
      };
      persistUser(minimalUserData);
      return minimalUserData;
    }

    // Paso 2: Obtener info del empleado + roles
    const employeeInfoRows = await getEmployeeInfo(loginData.employeeId);
    const firstRow = employeeInfoRows[0] || {};

    // Extraer roles únicos (una fila por rol)
    const roles = employeeInfoRows
      .filter((row) => row.roleId != null)
      .map((row) => ({ id: row.roleId, name: row.roleName, key: row.roleKey }));

    // Paso 3: Obtener locations asignadas
    const locationsData = await getEmployeeLocations(loginData.employeeId);
    const locations = locationsData.map((loc) => ({
      id: loc.locationId,
      key: loc.centerKey,
      name: loc.medicalCenter,
      isPrimary: loc.isPrimary,
      address: loc.addressOffice,
    }));
    const primaryLocation = locations.find((loc) => loc.isPrimary) || locations[0] || null;

    // Construir userData base con datos REALES del backend
    const userData = {
      token: loginData.token,
      employeeId: loginData.employeeId,
      sessionId: loginData.sessionId,
      forcePasswordChange: loginData.forcePasswordChange,
      passwordExpired: loginData.passwordExpired,
      firstName: firstRow.firstName,
      lastName: firstRow.lastName,
      fullName: `${firstRow.firstName} ${firstRow.lastName}`,
      email: firstRow.mail,
      businessEmail: firstRow.businessEmail,
      jobPositionId: firstRow.jobPositionId,
      jobPositionName: firstRow.jobPositionName,
      photo: firstRow.photo,
      // MFA status viene de SP_GET_EMPLOYEE_INFO (campo BIT_MFA_ENABLED).
      // Si el backend aún no expone el campo, quedará undefined y la UI lo
      // trata como "no activo".
      mfaEnabled: firstRow.mfaEnabled ?? false,
      mfaFrequencyDays: firstRow.mfaFrequencyDays ?? null,
      roles,
      activeRole: null,
      locations,
      primaryLocation,
      modules: [],
      // Legacy compatibility fields
      rol: roles[0]?.id?.toString() || null,
      location: primaryLocation?.key || null,
      employee: loginData.employeeId,
    };

    // Paso 4: Evaluar cantidad de roles
    if (roles.length === 1) {
      // Solo 1 rol → seleccionar automáticamente y cargar módulos
      const modulesData = await getRoleModules(loginData.employeeId, roles[0].id);
      const modules = Array.isArray(modulesData)
        ? modulesData.map((m) => ({
            id: m.moduleId,
            name: m.moduleName,
            key: m.moduleKey,
            canCreate: m.canCreate,
            canRead: m.canRead,
            canUpdate: m.canUpdate,
            canDelete: m.canDelete,
            hasAccess: m.hasAccess,
          }))
        : [];

      userData.activeRole = roles[0];
      userData.modules = modules;
      userData.rol = roles[0].id.toString();
    } else if (roles.length > 1) {
      // 2+ roles → marcar para selección en LoginPage
      userData.requireRoleSelection = true;
    }

    persistUser(userData);
    return userData;
  };

  /**
   * Sign in using AuthV2 endpoints + post-login MasterData calls.
   * Flujo según 10_LOGIN_SESSION_GUIDE.md + integración 2FA:
   *   1. Login (AuthV2) → puede retornar JWT completo O pre-auth token (MFA)
   *   2. Si mfaRequired=true → guardar preAuth state y retornar { mfaRequired }
   *      La LoginPage debe mostrar la pantalla del código TOTP
   *   3. Si no hay MFA → runPostLoginFlow (getEmployeeInfo, locations, modules)
   *
   * Returns:
   *   - { mfaRequired: true, ...data }  si el usuario tiene 2FA pendiente
   *   - { ...data, userData }           si el login fue directo
   *
   * On error, throws to let the caller handle UI feedback.
   */
  const signIn = async (email, password) => {
    const data = await loginV2({ email, password });

    // Caso A: credenciales inválidas o error de login → lanzar
    if (!data || data.result !== 0) {
      const error = new Error(data?.message || "Login failed");
      error.response = { data };
      throw error;
    }

    // Caso B: backend requiere verificación MFA (pre-auth token de 2 min)
    if (data.mfaRequired && data.preAuthToken) {
      // Guardar el pre-auth state SOLO en memoria. NUNCA persistir.
      setPreAuth({
        preAuthToken: data.preAuthToken,
        employeeId: data.employeeId,
        email,
        forcePasswordChange: data.forcePasswordChange,
        passwordExpired: data.passwordExpired,
      });
      // NO tocar localStorage todavía — el usuario aún no está autenticado
      return { ...data, mfaRequired: true };
    }

    // Caso C: login directo (sin MFA o dentro del período de frecuencia)
    if (data.token) {
      const userData = await runPostLoginFlow(data);
      return { ...data, userData };
    }

    // Defensivo: respuesta inesperada del backend
    const error = new Error("Respuesta de login inesperada");
    error.response = { data };
    throw error;
  };

  /**
   * Verificar código TOTP durante el login. Se llama después de que signIn
   * retornó mfaRequired=true. Usa el preAuthToken guardado en memoria.
   *
   * Si el código es válido, el backend emite el JWT completo y ejecutamos
   * el post-login flow normal (getEmployeeInfo, locations, modules).
   *
   * @param {string} totpCode - Código TOTP de 6 dígitos de la app authenticator
   * @returns {Object} userData completo
   * @throws Error si no hay preAuth activo o el código es inválido
   */
  const verifyMfaCode = async (totpCode) => {
    if (!preAuth?.preAuthToken) {
      const error = new Error("No hay sesión de verificación MFA activa. Vuelve a iniciar sesión.");
      error.code = "NO_PREAUTH";
      throw error;
    }

    const data = await verifyMfaApi(totpCode, preAuth.preAuthToken);

    if (!data?.success || !data.token) {
      const error = new Error(data?.message || "Código inválido");
      error.response = { data };
      throw error;
    }

    // Construir loginData para reusar el post-login flow
    const loginData = {
      token: data.token,
      employeeId: preAuth.employeeId,
      sessionId: null, // verify no retorna sessionId; se crea en el backend
      forcePasswordChange: preAuth.forcePasswordChange,
      passwordExpired: preAuth.passwordExpired,
    };

    const userData = await runPostLoginFlow(loginData);
    // Limpiar pre-auth state tras éxito
    setPreAuth(null);
    return userData;
  };

  /**
   * Usar un código de recuperación cuando el usuario perdió acceso a su
   * app authenticator. Consume uno de los 8 códigos generados en el enrollment.
   *
   * @param {string} recoveryCode - Código en formato xxxx-xxxx-xxxx
   * @returns {Object} { userData, codesRemaining, warning }
   * @throws Error si no hay preAuth activo o el código es inválido
   */
  const authWithRecoveryCode = async (recoveryCode) => {
    if (!preAuth?.preAuthToken) {
      const error = new Error("No hay sesión de verificación MFA activa. Vuelve a iniciar sesión.");
      error.code = "NO_PREAUTH";
      throw error;
    }

    const data = await consumeMfaRecoveryCode(recoveryCode, preAuth.preAuthToken);

    if (!data?.success || !data.token) {
      const error = new Error(data?.message || "Código de recuperación inválido");
      error.response = { data };
      throw error;
    }

    const loginData = {
      token: data.token,
      employeeId: preAuth.employeeId,
      sessionId: null,
      forcePasswordChange: preAuth.forcePasswordChange,
      passwordExpired: preAuth.passwordExpired,
    };

    const userData = await runPostLoginFlow(loginData);
    setPreAuth(null);

    // Si quedan pocos códigos, marcar en secureStorage para que el Layout
    // muestre el banner persistente hasta que el usuario re-enrole 2FA.
    const codesRemaining = data.codesRemaining ?? 0;
    if (codesRemaining <= 2) {
      secureStorage.setItem("mfa_low_codes_warning", { codesRemaining, shownAt: Date.now() });
    }

    return { userData, codesRemaining, warning: codesRemaining <= 2 };
  };

  /**
   * Descarta el pre-auth state. Se usa cuando el token de 2 min expira o
   * el usuario cancela la verificación MFA y vuelve al login.
   */
  const clearPreAuth = () => setPreAuth(null);

  /**
   * Actualiza localmente el flag `mfaEnabled` del user en memoria y storage
   * tras un enrollment/disable exitoso. Evita forzar un re-login para que el
   * UserProfile refleje el cambio inmediato.
   *
   * @param {boolean} enabled
   */
  const setMfaEnabled = (enabled) => {
    if (!user) return;
    const updated = { ...user, mfaEnabled: !!enabled };
    persistUser(updated);
  };

  /**
   * Seleccionar/cambiar rol activo y cargar módulos correspondientes.
   * Se usa después del login (si el usuario tiene 2+ roles) o durante la sesión
   * para cambiar de rol desde un dropdown.
   *
   * @param {number} roleId - ID del rol a activar
   */
  const switchRole = async (roleId) => {
    if (!user) throw new Error("No user session");

    const modulesData = await getRoleModules(user.employeeId, roleId);

    // Si getRoleModules retornó error (objeto con result > 0)
    if (!Array.isArray(modulesData) && modulesData?.result > 0) {
      const error = new Error(modulesData.message || "Failed to load role modules");
      error.response = { data: modulesData };
      throw error;
    }

    const modules = Array.isArray(modulesData)
      ? modulesData.map((m) => ({
          id: m.moduleId,
          name: m.moduleName,
          key: m.moduleKey,
          canCreate: m.canCreate,
          canRead: m.canRead,
          canUpdate: m.canUpdate,
          canDelete: m.canDelete,
          hasAccess: m.hasAccess,
        }))
      : [];

    const selectedRole = user.roles.find((r) => r.id === roleId) || null;

    const updatedUser = {
      ...user,
      activeRole: selectedRole,
      modules,
      requireRoleSelection: false,
      // Legacy compatibility
      rol: roleId.toString(),
    };

    persistUser(updatedUser);
    return updatedUser;
  };

  /**
   * Change password using AuthV2 endpoint.
   * Returns the response data so the ChangePasswordPage
   * can check result codes and display appropriate messages.
   */
  const changePassword = async (currentPassword, newPassword) => {
    const data = await changePasswordV2({ currentPassword, newPassword });
    return data;
  };

  /**
   * Reset password (admin action) - uses legacy endpoint.
   */
  const resetPassword = async (idEmployee, newPassword) => {
    try {
      const data = await resetPasswordService(idEmployee, newPassword);
      return data;
    } catch (error) {
      console.error("Error al resetear la contraseña");
      throw error;
    }
  };

  /**
   * Sign out - calls the new logout endpoint to invalidate session in DB,
   * then clears all local storage.
   */
  const signOut = async () => {
    // Intentar cerrar sesión en el backend (no bloquear si falla)
    try {
      const token = localStorage.getItem("token");
      if (token && !isTokenExpired(token)) {
        await logoutV2();
      }
    } catch (err) {
      // Ignorar errores de red en logout - limpiar de todos modos
    }

    setUser(null);
    setPreAuth(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    secureStorage.clear();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        signOut,
        changePassword,
        resetPassword,
        switchRole,
        // MFA
        preAuth,
        verifyMfaCode,
        authWithRecoveryCode,
        clearPreAuth,
        setMfaEnabled,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
